import React, { useMemo, useState } from 'react'
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  useTheme,
  Radio,
  RadioGroup,
  FormControlLabel,
  Tooltip,
} from '@material-ui/core'
import { Search as SearchIcon } from '@material-ui/icons'
import { ArrowUpward, ArrowDownward } from '@material-ui/icons'
import { motion } from 'framer-motion'
import {
  StyledTableContainerWithScroll,
  FilterControlsContainer,
  FilterLabel,
  FilterChipContainer,
  FilterChip,
  EmptyStateBox,
  FileTypeChip,
} from '../styles/StyledComponents'
import { useBedFiles } from '../../../../api/file/list'

const BedPanelSelect = ({ defaultSelectedName = '', onSelectionChange }) => {
  const theme = useTheme()
  const {
    query: { data: bedFilesData = [], isLoading },
  } = useBedFiles()

  const [searchTerm, setSearchTerm] = useState('')
  const [liftoverFilter, setLiftoverFilter] = useState('All')
  const [verificationFilter, setVerificationFilter] = useState('All')
  const [sortOrder, setSortOrder] = useState('desc')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [selectedName, setSelectedName] = useState(defaultSelectedName)

  const filtered = useMemo(() => {
    let list = [...(bedFilesData || [])]

    if (verificationFilter !== 'All') {
      const want = verificationFilter === 'Verified'
      list = list.filter((b) => !!b.is_verified === want)
    }
    if (liftoverFilter !== 'All') {
      const wantLift = liftoverFilter === 'Yes'
      list = list.filter((b) => !!b.liftovered === wantLift)
    }
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase()
      list = list.filter((b) => {
        const a = (b.visible_name || '').toLowerCase()
        const c = (b.name || '').toLowerCase()
        const d = (b.original_name || '').toLowerCase()
        return a.includes(q) || c.includes(q) || d.includes(q)
      })
    }
    list.sort((a, b) => {
      const da = a.modified || a.created || ''
      const db = b.modified || b.created || ''
      const va = da ? new Date(da).getTime() : 0
      const vb = db ? new Date(db).getTime() : 0
      return sortOrder === 'desc' ? vb - va : va - vb
    })
    return list
  }, [bedFilesData, liftoverFilter, verificationFilter, searchTerm, sortOrder])

  const paginated = useMemo(() => {
    const start = page * rowsPerPage
    return filtered.slice(start, start + rowsPerPage)
  }, [filtered, page, rowsPerPage])

  const formatDateTime = (value) => {
    if (!value) return '·'
    const d = new Date(value)
    if (isNaN(d.getTime())) return '·'
    const dateStr = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    const timeStr = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false })
    return `${dateStr} ${timeStr}`
  }

  const handleSelectChange = (value) => {
    setSelectedName(value)
    if (onSelectionChange) onSelectionChange(value)
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box
        sx={{
          p: 3,
          borderRadius: 2,
          border: (t) => `1px solid ${t.palette.divider}`,
          backgroundColor: (t) => t.palette.background.paper,
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" sx={{ color: theme.palette.primary.main, fontWeight: 600, mb: 0.5 }} align="center">
            Select Capture Kit (BED Panel)
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 1.5 }} align="center">
            Choose a BED panel to filter cohort files.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
          <Box sx={{ flex: 1, minWidth: 260, maxWidth: 420 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by panel name or file name"
              value={searchTerm}
              onChange={(e) => {
                setPage(0)
                setSearchTerm(e.target.value)
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: theme.palette.text.secondary, fontSize: '1.1rem' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <FilterControlsContainer>
            <FilterLabel variant="body2">Liftover:</FilterLabel>
            <FilterChipContainer>
              {['All', 'Yes', 'No'].map((v) => (
                <FilterChip key={v} active={liftoverFilter === v} onClick={() => setLiftoverFilter(v)}>
                  {v}
                </FilterChip>
              ))}
            </FilterChipContainer>
          </FilterControlsContainer>

          <FilterControlsContainer>
            <FilterLabel variant="body2">Verification:</FilterLabel>
            <FilterChipContainer>
              {['All', 'Verified', 'Unverified'].map((v) => (
                <FilterChip key={v} active={verificationFilter === v} onClick={() => setVerificationFilter(v)}>
                  {v}
                </FilterChip>
              ))}
            </FilterChipContainer>
          </FilterControlsContainer>
        </Box>

        <RadioGroup value={selectedName} onChange={(e) => handleSelectChange(e.target.value)}>
          <StyledTableContainerWithScroll component={Paper} sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Created At
                      </Typography>
                      <Box
                        role="button"
                        tabIndex={0}
                        aria-label="Toggle sort by Created At"
                        onClick={() => setSortOrder((s) => (s === 'desc' ? 'asc' : 'desc'))}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') setSortOrder((s) => (s === 'desc' ? 'asc' : 'desc'))
                        }}
                        style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                      >
                        {sortOrder === 'desc' ? (
                          <ArrowDownward style={{ fontSize: 16, color: theme.palette.text.secondary }} />
                        ) : (
                          <ArrowUpward style={{ fontSize: 16, color: theme.palette.text.secondary }} />
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Verification</TableCell>
                  <TableCell>Reference</TableCell>
                  <TableCell>Liftover</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading && (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Typography variant="body2">Loading...</Typography>
                    </TableCell>
                  </TableRow>
                )}
                {!isLoading && paginated.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <EmptyStateBox>
                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                          No BED panels found
                        </Typography>
                      </EmptyStateBox>
                    </TableCell>
                  </TableRow>
                )}
                {paginated.map((bed, index) => {
                  const label = bed.visible_name || bed.name
                  const selected = selectedName === bed.name
                  return (
                    <TableRow
                      key={bed.id || bed.name}
                      className={`${selected ? 'selected' : ''}`}
                      component={motion.tr}
                      custom={index}
                      initial="initial"
                      animate="animate"
                      onClick={() => {
                        if (bed.is_verified) handleSelectChange(bed.name)
                      }}
                      onKeyDown={(e) => {
                        if (!bed.is_verified) return
                        if (e.key === 'Enter' || e.key === ' ') handleSelectChange(bed.name)
                      }}
                      tabIndex={0}
                      sx={{
                        cursor: bed.is_verified ? 'pointer' : 'not-allowed',
                        opacity: bed.is_verified ? 1 : 0.6,
                        filter: bed.is_verified ? 'none' : 'grayscale(20%)',
                      }}
                    >
                      <TableCell>
                        <FormControlLabel
                          value={bed.name}
                          control={<Radio color="primary" disabled={!bed.is_verified} />}
                          label={
                            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                              {formatDateTime(bed.created)}
                            </Typography>
                          }
                          sx={{ margin: 0 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                          <Tooltip title={bed.original_name || bed.name} arrow placement="top">
                            <Typography
                              variant="body2"
                              sx={{
                                px: 1.5,
                                py: 0.5,
                                borderRadius: 2,
                                backgroundColor: theme.palette.action.selected,
                                maxWidth: 360,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {label}
                            </Typography>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {bed.is_verified ? (
                          <FileTypeChip fileType="verified">Verified</FileTypeChip>
                        ) : (
                          <FileTypeChip fileType="unverified">Unverified</FileTypeChip>
                        )}
                      </TableCell>
                      <TableCell>{bed.reference || '·'}</TableCell>
                      <TableCell>
                        {bed.liftovered ? (
                          <FileTypeChip fileType="liftover">Yes</FileTypeChip>
                        ) : (
                          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                            ·
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={filtered.length}
              page={page}
              onPageChange={(e, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10))
                setPage(0)
              }}
              rowsPerPageOptions={[10, 25, 50]}
              labelRowsPerPage="Rows per page:"
            />
          </StyledTableContainerWithScroll>
        </RadioGroup>
      </Box>
    </Box>
  )
}

export default BedPanelSelect
